import type { ChoiceValue } from 'fitify-ui-onboarding/src/components/GenderFunnel/ChoiceCard.Types'
import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import {
  logAnswerEvent,
  logArrayAnswerEvent,
} from 'fitify-ui-onboarding/src/utils/analytics'
import { getPaging } from 'fitify-ui-onboarding/src/utils/navigation'
import { isDefined } from 'fitify-utils/src/properties'
import { useCallback } from 'react'

import type { FunnelDataPath } from 'types/state'
import { useFunnelState } from 'utils/state'

import type { ChoiceCardPageType } from './ChoiceCardPage.Types'

type Props<TValue extends ChoiceValue, TFunnel extends FunnelDataPath> = Pick<
  ChoiceCardPageType<TValue, TFunnel>,
  | 'analyticsMapping'
  | 'funnel'
  | 'isMultiChoice'
  | 'onChange'
  | 'options'
  | 'value'
>

type UseChoicePage<TValue extends ChoiceValue> = {
  handleChange: (option: TValue) => void
  handleContinue: () => void
  isValid: boolean
}

export const useChoicePage = <
  TValue extends ChoiceValue,
  TFunnel extends FunnelDataPath,
>({
  analyticsMapping,
  funnel,
  isMultiChoice,
  onChange,
  options,
  value,
}: Props<TValue, TFunnel>): UseChoicePage<TValue> => {
  const router = useRouter()
  const { data, set } = useFunnelState(funnel)

  const isValid = Boolean(value && value.length > 0)

  const updateValue = useCallback(
    (option: TValue) => {
      const array = [...value]
      const indexOfOptionInValue = array.indexOf(option)
      const isOptionSelected = indexOfOptionInValue > -1

      if (isOptionSelected) {
        // Deselect option only in multi choice mode
        if (isMultiChoice) {
          array.splice(indexOfOptionInValue, 1)
        }
        return onChange(array, option)
      } else if (isMultiChoice) {
        return onChange(array.concat(option), option)
      } else {
        return onChange([option], option)
      }
    },
    [isMultiChoice, onChange, value]
  )

  const handleChange = useCallback(
    (option: TValue) => {
      const updatedData = updateValue(option)
      set(updatedData)

      const shouldProceedToNextStep = !isMultiChoice
      if (shouldProceedToNextStep) {
        const paging = getPaging({
          path: router.resolvedPathname,
          data: { ...data, ...updatedData },
        })
        logAnswerEvent(paging.current, analyticsMapping?.[option] ?? option)
        if (paging.next) {
          void router.push(paging.next.path)
        }
      }
    },
    [analyticsMapping, data, isMultiChoice, router, set, updateValue]
  )

  const handleContinue = useCallback(() => {
    const paging = getPaging({ path: router.resolvedPathname, data })

    if (isMultiChoice) {
      logArrayAnswerEvent(
        paging.current,
        options.map(
          (option) => analyticsMapping?.[option.value] ?? option.value
        ),
        value.map((item) => analyticsMapping?.[item] ?? item)
      )
    } else {
      const item = value[0]
      const analyticsValue = isDefined(item)
        ? (analyticsMapping?.[item] ?? item)
        : item
      logAnswerEvent(paging.current, analyticsValue)
    }

    if (paging.next && value.length) {
      void router.push(paging.next.path)
    }
  }, [analyticsMapping, data, isMultiChoice, options, router, value])

  return {
    handleChange,
    handleContinue,
    isValid,
  }
}
